// Core
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { 
  Box,
  Button,
  Flex,
  Spinner,
  Image,
  Input,
  Grid,
  Text,
  useDisclosure,
  useColorModeValue,
  useBreakpointValue
} from '@chakra-ui/react';

// Assets
import { ReactComponent as WarningIcon } from 'assets/svg/warning-v3.svg';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import crownImage from 'assets/img/crown.png';
import coinImage from 'assets/img/details/coin-1.png';
import boxImage from 'assets/img/login/box.png';
import box2Image from 'assets/img/details/box.png';
import upcomingCancellingImage from 'assets/img/cash-house.png';
import galleryBgLeft from 'assets/img/details/gallery-bg-left.png';
import galleryBgRight from 'assets/img/details/gallery-bg-right.png';
import glow13Image from 'assets/img/glow-13.png';
import glow14Image from 'assets/img/glow-14.png';

// Components
import WarningCard from 'components/Form/WarningCritical';
import UpcomingCancelling from './UpcomingCancelling';
import PartnersDoubles from 'components/Sliders/PartnersDoubles';

const PendingTag = (props) => {
  return (
    <Flex alignItems="center" m={props.m ?? '0 auto'} 
      borderRadius="16px" gap="8px" p="4px 4px 4px 4px" 
      w={{base: '226px', md: '275px'}}
      zIndex="1"
      bg="white" justifyContent="center">
      <Box w="24px"><WarningIcon fontSize="24px" /></Box>
      <Text 
        fontSize={{base: '20px', md: '24px'}}
        fontStyle="italic"
        fontWeight="700"
        lineHeight="100%"
        pr="4px"
        sx={{
          background: 'linear-gradient(180deg, #FD2A2A 0%, #AF0000 100%)',
          backgroundClip: 'text',
          '-webkit-background-clip': 'text',
          '-webkit-text-fill-color': 'transparent'
        }}
        letterSpacing="-1px"
        textTransform="uppercase"
        >Pending Deletion</Text>
    </Flex>
  );
};

const PendingDeletion = ({
  userData,
  keepsAnotherPlan
}) => {
  const coinsShadow = useColorModeValue('#E0EAFF', '#099BFF');
  const galleryShadow = useColorModeValue('#E0EAFF', '#00071C');
  const cardShadow = useColorModeValue('0 4px 30px 0 rgba(255, 255, 255, 0.50)', '0 4px 30px 0 rgba(0, 0, 0, 0.50)');
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTablet = useBreakpointValue({ base: false, sm: true, md: false });

  // Plan list or profile still loading: show neither wording until it is known
  // whether another plan remains.
  if (keepsAnotherPlan === undefined) {
    return (
      <Flex justifyContent="center" alignItems="center" minH="320px">
        <Spinner size="xl" color="#099BFF" thickness="4px" />
      </Flex>
    );
  }

  return (
  	<> 
      <Text variant="header" fontSize={{base: '36px', md: '44px', lg: '56px'}} textAlign="center" 
        mb={{ base: '12px', md: '24px' }}
        lineHeight={{base: '100%', md: 'initial'}}
        >
        Are you sure you want to <br /><Text variant="header-gradient" lineHeight={{base: '100%', md: 'initial'}} fontSize={{base: '36px', md: '44px', lg: '56px'}} as="span">{keepsAnotherPlan ? 'Cancel This Plan?' : 'Delete Your Entries?'}</Text>
      </Text>

      <WarningCard
        maxW="936px"
        m="0 auto" 
        text={keepsAnotherPlan
          ? 'Canceling this plan will forfeit its discounts and eligibility for future giveaways worth millions of dollars. You will no longer be in the draw for any upcoming prizes with this subscription.'
          : 'Canceling your membership will forfeit your discounts, accumulated entries, and eligibility for future giveaways worth millions of dollars. You will no longer be in the draw for any upcoming prizes with this subscription.'} />
    
      {/* Entries and coins are account-wide: only shown as lost when no other live plan remains. */}
      { !keepsAnotherPlan &&
      <Box position="relative" mt={{ base: '28px', md: '36px'}}>
        <PendingTag />

        <Box position="relative">
          <Box 
            zIndex="2"
            overflow="hidden"
            bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
            m={{ base: '21px auto 0 auto', md: '30px auto 0 auto'}}
            maxW={{base: '285px', md: '410px'}}
            w="100%"
            borderRadius="16px"
            boxShadow={cardShadow}
            position="relative">
            <Box
              p={{ base: '12px 16px', md: '24px 16px' }}
              h={{ base: '103px', md: '148px' }}
              borderRadius="16px"
              >
              <Text
                mt="0"
                color="#FFF"
                textAlign="center"
                fontSize={{ base: '40px', md: '64px'}}
                fontStyle="normal"
                fontWeight="800"
                lineHeight="100%"
                textTransform="uppercase"
                >{userData ? (userData.entries ?? 0) : ''}</Text>

              <Box maxW={{ base: '206px', md: '260px'}} 
                w="100%" 
                borderRadius="16px" 
                bg="#FFF" 
                m="12px auto 0 auto" textAlign="center">
                <Text
                  fontSize={{ base: '16px', md: '20px' }}
                  fontStyle="italic"
                  fontWeight="900"
                  lineHeight="120%"
                  letterSpacing="-1px"
                  textTransform="uppercase"
                  bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                  sx={{
                    'background-clip': 'text',
                    '-webkit-background-clip': 'text',
                    '-webkit-text-fill-color': 'transparent'
                  }}
                  >Accumulated Entries</Text>
              </Box>
            </Box>

            <Lines className="abstract-lines" style={{zIndex: 1, left: '-20%', transform: 'scale(0.8)'}} />
          </Box>

          <Box 
            zIndex="2"
            overflow="hidden"
            bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
            m="8px auto 0 auto"
            maxW={{base: '285px', md: '410px'}}
            borderRadius="16px"
            boxShadow={cardShadow}
            position="relative">
            <Box
              p={{ base: '12px 16px', md: '24px 16px' }}
              h={{ base: '103px', md: '148px' }}
              borderRadius="16px"
              >
              <Text
                color="#FFF"
                textAlign="center"
                fontSize={{ base: userData?.cashbackBalance > 999999 ? '24px' : '40px', md: userData?.cashbackBalance > 999999 ? '44px' : '64px' }}
                mt={{ base: userData?.cashbackBalance > 999999 ? '10px' : '0px', md: userData?.cashbackBalance > 999999 ? '8px' : '0px' }}
                fontStyle="normal"
                fontWeight="800"
                lineHeight="100%"
                textTransform="uppercase"
                >{userData?.cashbackBalance}</Text>

              <Box w="100%"
                maxW={{ base: '137px', md: '170px'}} 
                borderRadius="16px" bg="#FFF" m="12px auto 0 auto" textAlign="center">
                <Text
                  fontSize={{ base: '16px', md: '20px' }}
                  fontStyle="italic"
                  fontWeight="900"
                  lineHeight="120%"
                  letterSpacing="-1px"
                  textTransform="uppercase"
                  bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                  sx={{
                    'background-clip': 'text',
                    '-webkit-background-clip': 'text',
                    '-webkit-text-fill-color': 'transparent'
                  }}
                  >Coin Balance</Text>
              </Box>
            </Box>

            <Lines className="abstract-lines" style={{zIndex: 1, left: '-20%', transform: 'scale(0.8)'}} />
          </Box>

          { !isMobile ?
          <Box position="absolute" w="100%" top="0px" zIndex="1">
            <Box w="340px" h="200px" borderRadius="full" filter="blur(100px)" bg={coinsShadow} left="270px" position="absolute" top="130px" zIndex="21" />
            <Box w="270px" h="150px" borderRadius="full" filter="blur(100px)" bg={coinsShadow} left="100px" position="absolute" top="100px" zIndex="17" />
            <Image src={crownImage} alt="Crown" zIndex="20" position="absolute" top="-42px" left="-65px" w="512px" h="512px" objectFit="contain" />
            <Image src={crownImage} alt="Crown" zIndex="20" position="absolute" top="-42px" right="-65px" w="512px" h="512px" objectFit="contain" transform="rotateY(180deg)" />
            <Image 
              display={{md: 'none', lg: 'block'}} 
              src={crownImage} alt="Crown" zIndex="1" position="absolute" top="-450px" left="540px" w="700px" h="700px" objectFit="contain" transform="rotate(-40deg) translate(-72%, 30px)" />
            
            <Image src={coinImage} alt="Coin" zIndex="18" position="absolute" top="83px" left="90px" w="170px" h="170px" objectFit="contain" transform="rotate(30deg)" />
            <Image src={coinImage} alt="Coin" zIndex="16" position="absolute" top="-30px" left="150px" w="300px" h="300px" objectFit="contain" transform="rotate(150deg) rotateX(180deg)" />
            <Image src={coinImage} alt="Coin" zIndex="16" position="absolute" top="13px" left="120px" w="60px" h="60px" objectFit="contain" transform="rotate(150deg) rotateX(180deg)" filter="blur(2px)" />
            <Image src={coinImage} alt="Coin" zIndex="16" position="absolute" top="15px" left="290px" w="50px" h="50px" objectFit="contain" transform="rotate(80deg) rotateX(180deg)" filter="blur(2px)" />
            <Image src={boxImage} alt="Box" zIndex="17" position="absolute" top="49px" left="140px" w="280px" h="280px" objectFit="contain" transform="rotate(-15deg)"/>

            <Box w="340px" h="200px" borderRadius="full" filter="blur(100px)" bg={coinsShadow} right="270px" position="absolute" top="130px" zIndex="21" />
            <Box w="210px" h="120px" borderRadius="full" filter="blur(50px)" bg={coinsShadow} right="100px" position="absolute" top="50px" zIndex="17" />
            <Image src={coinImage} alt="Coin" zIndex="17" position="absolute" top="45px" right="60px" w="200px" h="190px" objectFit="contain" transform="rotate(30deg)" />
            <Image src={coinImage} alt="Coin" zIndex="17" position="absolute" top="-20px" right="170px" w="300px" h="300px" objectFit="contain" transform="rotate(21deg)" />
            <Image src={coinImage} alt="Coin" zIndex="16" position="absolute" top="-7px" right="120px" w="60px" h="60px" objectFit="contain" transform="rotate(50deg)" filter="blur(2px)" />
            <Image src={coinImage} alt="Coin" zIndex="17" position="absolute" top="45px" right="290px" w="50px" h="50px" objectFit="contain" transform="rotate(80deg)" filter="blur(2px)" />
            <Image src={coinImage} alt="Coin" zIndex="11" position="absolute" top="20px" right="150px" w="130px" h="130px" objectFit="contain" transform="rotate(30deg)" />
            <Image src={box2Image} alt="Box" zIndex="17" position="absolute" top="89px" right="136px" w="250px" h="280px" objectFit="contain" transform="rotate(11deg) rotateY(180deg)"/>
          </Box>
          :
          <Image 
            src={ isTablet ? glow14Image : glow13Image } 
            alt="Balances" 
            position="absolute" 
            maxW={{base: '200%', sm: '120%'}} 
            w={{base: '200%', sm: '120%'}} 
            left={{base: '-50%', sm: '-10%'}} 
            top={{base: '-60px', sm: 'unset'}} 
            bottom={{base: 'unset', sm: '-60% !important'}} 
            zIndex="1" />
          }
        </Box>
      </Box>
      }

      <Box mt="240px" position="relative">
        <Image 
          src={upcomingCancellingImage} 
          m={{base: '-80px auto 0 auto', md: '-111px auto 0 auto' }}
          position="absolute"
          left="50%"
          maxW={{ base: '380px', md: '670px', lg: '800px'}}
          transform="translate(-50%, 0px)"
          alt="Upcoming Cancelling" />
        <Box 
          bg={galleryShadow} 
          w={{ base: '200%', md: '100%'}}
          m={{base: '-25% 0 0 -50%', sm: '-10% 0 0 -50%', md: '-10% 0 0 0%', lg: '0'}} 
          h="600px" 
          position="absolute" filter="blur(70px)" 
          borderRadius="200px 200px 0 0" />

        <Box position="relative" zIndex="2" mt={{base: '-55px', md: '0'}}>
          <Text textAlign="center" variant="header" fontSize={{base: '36px', md: '44px', lg: '56px'}}>
            <Text as="span" variant="header-gradient" fontSize={{base: '36px', md: '44px', lg: '56px'}}>
            $20,000,000+</Text> In Upcoming Giveaways
          </Text>

          <Text textAlign="center" maxW="590px" 
            m={{base: '16px auto 24px auto', md: '20px auto 32px auto'}} 
            variant="header" fontSize="24px">Cancelling will remove your loyalty points from all future giveaways like this.</Text>
        
          <PendingTag />

          <UpcomingCancelling userData={userData} />
        </Box>
      </Box>

      <Box 
        mt={{base: '60px', md: '80px'}} 
        position="relative">
        <Box position="absolute" zIndex="-1" w="100%" top="20px" opacity={{base: '0.2', md: '1'}}>
          <Image src={galleryBgLeft} alt="Gallery" position="absolute" objectFit="contain" w="100%" h="760px" left="-30%" top="17px" />
          <Image src={galleryBgRight} alt="Gallery" position="absolute" objectFit="contain" w="100%" h="760px" left="unset" right="-30.5%" top="-9px" />
          <Box bg={galleryShadow} mt="60px" zIndex="1" filter="blur(130px)" borderRadius="100%" w="100%" h="390px" position="absolute" />
        </Box>

        <Box position="relative" zIndex="2">
          <Text textAlign="center" variant="header" fontSize={{base: '36px', md: '44px', lg: '56px'}}>
            <Text as="span" variant="header-gradient" fontSize={{base: '36px', md: '44px', lg: '56px'}}>No More Discounts</Text> At Over
            <br /><Text as="span" variant="header-gradient" fontSize={{base: '36px', md: '44px', lg: '56px'}}>2,000+</Text> Business
          </Text>

          <Text textAlign="center" maxW="590px" 
            m={{base: '16px auto 24px auto', md: '20px auto 32px auto'}} 
            variant="header" fontSize="24px">
            With an ever-growing list of partners you can save more than your membership fee.
          </Text>
        
          <PendingTag m={{ base: '0 auto -12px auto', md: '0 auto 44px auto'}} />

          <Box h={{ base: '229px', md: '349px'}} position="relative">
            <Box position="absolute" left="50%" 
              transform={{base: 'translate(-50%, 0px)', md: 'translate(-50%, -38px)'}} 
              className="modal_full-screen-partners-doubles">
              <PartnersDoubles />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PendingDeletion;